"use client"
import styled from "@emotion/styled"
import { Box, Drawer, IconButton } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useState } from "react"
import "swiper/css"
import "swiper/css/autoplay"
import { Autoplay, EffectFade, Navigation } from "swiper/modules"
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react"
import {
  Container,
  Desktop,
  GalleryIcon,
  LeftArrow,
  LeftArrowIos,
  Mobile,
  NewText,
  RightArrow,
  RightArrowIos,
  StartIcon,
  StopSign,
  transitionTime,
  useIsDesktop,
} from "ui"
import { sharedColors as colors } from "ui/theme/colors"
import { getUmbracoMediaUrl } from "utils"
import { StyledExpandIcon } from "../ApartmentsForRent/ApartmentGallery"
import SliderExpand from "./SliderExpand"
import SliderVideoComp from "./SliderVideoComp"

const StyledExpandIconLocal = styled(StyledExpandIcon)`
  position: absolute;
  top: 16px;
  inset-inline-start: 16px;
  background-color: rgba(255, 255, 255, 0.5);
  &:hover {
    background-color: rgba(255, 255, 255, 1);
  }
`

const StyledPaginatorContainer = styled.div`
  position: absolute;
  top: 19px;
  left: 16px;
  z-index: 10;
  background-color: white;
  padding: 10px;
  border-radius: 200px;

  ${(props) => props.theme.breakpoints.up("md")} {
    top: unset;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 3px 6px;
    border-radius: 200px;
  }
`

const StyledBulletsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6.7px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
  }
  justify-content: flex-start;
  align-items: center;
  margin: 0 4px;
`
const StyledPlayButton = styled.div`
  display: flex;
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
  gap: 4.923px;
  border-radius: 123.077px;
  background-color: ${colors.white};
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);
  position: absolute;
  right: 25px;
  ${(props) => props.theme.breakpoints.up("lg")} {
    right: 10px;
  }
  top: 10px;

  cursor: pointer;
  z-index: 30;
`
const StyledBullet = styled.div<{ active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin: 0 2px;
  cursor: pointer;
  border: ${(props) =>
    props.active
      ? `1.667px solid ${props.theme.colors.bulletsBackgoundColor}`
      : "1.667px solid black"};
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 14px;
    height: 14px;
    border: ${(props) =>
      props.active
        ? `2.333px solid ${props.theme.colors.bulletsBackgoundColor}`
        : "2.333px solid black"};
  }
  background-color: ${(props) =>
    props.active ? props.theme.colors.bulletsBackgoundColor : "none"};
`
const StyledBulletLine = styled.div<{ active: boolean }>`
  width: ${(props) => (props.active ? "26px" : "13px")};
  height: 2px;
  border-radius: 8px;
  margin: 0 8px;
  cursor: pointer;
  background-color: ${(props) =>
    props.active
      ? props.theme.colors.bulletsBackgroundColor4rentMarketingActive
      : props.theme.colors.bulletsBackgroundColor4rentMarketing};

  transition: all 0.3s ease;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${(props) => (props.active ? "40px" : "20px")};
    height: 2px;
  }
`

const StyledSwiperSlide = styled(SwiperSlide)<{ slideHeight?: number; mobileSlideHeight?: number }>`
  ${({ slideHeight, mobileSlideHeight }) => `
    height: ${slideHeight}px !important;
    @media (max-width: 768px) {
      height: ${mobileSlideHeight}px !important;
    }
  `}
  > div {
    height: 100%;
  }
`
const StyledNavItem = styled.div<{ alwaysSmallNav?: boolean }>`
  --padding-space: 5px;
  --bg-image-left: url("/icons/gallery-arrow-bg-left.svg");
  --bg-image-right: url("/icons/gallery-arrow-bg-right.svg");
  position: absolute;
  inset-block-start: 50%;
  inset-inline-end: 20px;
  transform: translateY(-50%);
  z-index: 1;
  width: 42px;
  aspect-ratio: 80/48;
  background-repeat: no-repeat;
  background-size: contain;

  display: flex;
  align-items: center;
  cursor: pointer;
  svg {
    width: 14px;
    height: 14px;
    &:hover {
      color: ${(props) => props.theme.colors.blue4rentMarketing};
    }
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    ${(props) =>
      props.alwaysSmallNav
        ? ""
        : `
        --padding-space: 12px;
        width: 80px;
        height: 48px;
        svg {
          width: 24px;
          height: 24px;
        }
    `}
  }
`
const StyledNavPrev = styled(StyledNavItem)<{ locale: string }>`
  inset-inline-start: -1px;
  background-image: ${(props) =>
    props.locale === "he" ? "var(--bg-image-left)" : "var(--bg-image-left)"};
  padding-inline-end: var(--padding-space);
  justify-content: flex-end;
`
const StyledNavNext = styled(StyledNavItem)<{ locale: string }>`
  inset-inline-end: -1px;
  background-image: ${(props) =>
    props.locale === "he" ? "var(--bg-image-right)" : "var(--bg-image-right)"};

  padding-inline-start: var(--padding-space);
`

const NewStyledPaginator = styled.div<{ locale: string }>`
  padding-block: 8px;
  margin-block-start: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  /* flex-direction: ${(props) => (props.locale === "he" ? "row-reverse" : "row")}; */
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-block-start: 16px;
  }
`

const StyledGalleryDrawer = styled(Drawer)`
  box-shadow: none;
  border: none;
  ::before {
    content: none;
  }
`

type Props = {
  locale: string
  gallery: Array<{
    imageOrVideoSelectorMobaile?: string
    imageOrVideoSelsctor?: string
  }>
  swiperContainerProps?: {
    [key: string]: any
  }
  newStyle?: boolean
  slideHeight?: number
  mobileSlideHeight?: number
  expandIcon?: boolean
  swiperBorderRadius?: number
  numberOfInterest?: string | null
}
const Slider = ({
  gallery,
  locale,
  swiperContainerProps,
  newStyle,
  slideHeight,
  mobileSlideHeight,
  expandIcon,
  swiperBorderRadius,
  numberOfInterest,
}: Props) => {
  const t = useTranslations("AccordionDetails")
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [swiperRef, setSwiperRef] = useState<SwiperClass | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const toggleAutoplay = () => {
    if (swiperRef) {
      if (isPlaying) {
        swiperRef.autoplay.stop()
      } else {
        swiperRef.autoplay.start()
      }
      setIsPlaying(!isPlaying)
    }
  }
  const isDesktop = useIsDesktop()

  const activeGallery = gallery
    ?.map((item) => item.imageOrVideoSelectorMobaile || item.imageOrVideoSelsctor)
    .filter(Boolean) as string[]
  return (
    <Container style={{ position: "relative" }} {...swiperContainerProps}>
      <Swiper
        dir={locale === "he" ? "rtl" : "ltr"}
        modules={[Autoplay, EffectFade, ...(newStyle ? [Navigation] : [])]}
        onSwiper={setSwiperRef}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.activeIndex)
        }}
        navigation={{ nextEl: ".swiper-button-next", prevEl: ".swiper-button-prev" }}
        speed={transitionTime * 1000}
        autoplay={
          isPlaying
            ? {
                delay: 4000,
                disableOnInteraction: false,
              }
            : false
        }
        style={{
          padding: 0,
          borderRadius: swiperBorderRadius || 16,
          position: "relative",
        }}
        watchSlidesProgress
        effect="fade"
      >
        {gallery.map((item, key: number) => (
          <StyledSwiperSlide
            key={key.toString()}
            style={{
              width: "100%",
              height: newStyle ? (isDesktop ? "458px" : "217px") : isDesktop ? "560px" : "460px",
            }}
            slideHeight={slideHeight}
            mobileSlideHeight={mobileSlideHeight}
          >
            <Desktop>
              {item.imageOrVideoSelsctor &&
                (!item.imageOrVideoSelsctor?.includes(".mp4") ? (
                  <Image
                    src={getUmbracoMediaUrl(item.imageOrVideoSelsctor)}
                    alt={`product-image-${key + 1}`}
                    fill
                    style={{
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <SliderVideoComp url={getUmbracoMediaUrl(item.imageOrVideoSelsctor)} />
                ))}
            </Desktop>
            <Mobile>
              {item.imageOrVideoSelectorMobaile &&
                (!item.imageOrVideoSelectorMobaile?.includes(".mp4") ? (
                  <Image
                    src={getUmbracoMediaUrl(item.imageOrVideoSelectorMobaile)}
                    alt={`product-image-${key + 1}`}
                    fill
                    style={{
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <SliderVideoComp url={getUmbracoMediaUrl(item.imageOrVideoSelectorMobaile)} />
                ))}
            </Mobile>

            {/* Render the image or video based on the selector provided */}
          </StyledSwiperSlide>
        ))}

        {!newStyle && (
          <StyledPaginatorContainer>
            <Desktop>
              <IconButton
                onClick={() => {
                  swiperRef?.slidePrev()
                }}
              >
                {locale === "he" ? <RightArrow /> : <LeftArrow />}
              </IconButton>
            </Desktop>
            <StyledBulletsContainer>
              {gallery.map((_: any, index: number) => (
                <StyledBullet
                  key={index.toString()}
                  onClick={() => {
                    swiperRef?.slideTo(index)
                  }}
                  active={index === activeIndex}
                />
              ))}
            </StyledBulletsContainer>
            <Desktop>
              <IconButton
                onClick={() => {
                  swiperRef?.slideNext()
                }}
              >
                {locale === "he" ? <LeftArrow /> : <RightArrow />}
              </IconButton>
            </Desktop>
          </StyledPaginatorContainer>
        )}
        {newStyle && (
          <>
            {expandIcon && (
              <StyledExpandIconLocal>
                <IconButton onClick={() => setIsExpanded(true)}>
                  <GalleryIcon />
                </IconButton>
              </StyledExpandIconLocal>
            )}
            {gallery?.length > 1 && (
              <>
                <StyledNavNext
                  locale={locale}
                  alwaysSmallNav={
                    newStyle && mobileSlideHeight && mobileSlideHeight > 0 ? true : false
                  }
                  onClick={() => {
                    swiperRef?.slideNext()
                  }}
                >
                  {locale === "he" ? <LeftArrowIos /> : <RightArrowIos />}
                </StyledNavNext>
                <StyledNavPrev
                  locale={locale}
                  alwaysSmallNav={
                    newStyle && mobileSlideHeight && mobileSlideHeight > 0 ? true : false
                  }
                  onClick={() => {
                    swiperRef?.slidePrev()
                  }}
                >
                  {locale === "he" ? <RightArrowIos /> : <LeftArrowIos />}
                </StyledNavPrev>
              </>
            )}
          </>
        )}
      </Swiper>
      {numberOfInterest && (
        <Box
          sx={{
            position: "absolute",
            bottom: "50px",
            insetInlineStart: "50px",
            padding: "4px 16px",
            display: "flex",
            gap: "8px",
            alignItems: "center",
            backgroundColor: "#1F375D99",
            borderRadius: "22px",
            color: "white",
            zIndex: 100000,
          }}
        >
          <NewText
            component="p"
            color="white"
            desktopFontSize={16}
            mobileFontSize={16}
            desktopLineHeight={"150%"}
            mobileLineHeight={"150%"}
            mobileFontWeight={300}
            desktopFontWeight={300}
          >
            {t("numberOfInterested", { count: numberOfInterest })}
          </NewText>
          <Box
            sx={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: "#63D679",
              display: "flex",
            }}
          />
        </Box>
      )}
      {!!newStyle && (
        <NewStyledPaginator locale={locale} dir={locale === "he" ? "rtl" : "ltr"}>
          {gallery.map((_: any, index: number) => (
            <StyledBulletLine
              key={index.toString()}
              onClick={() => {
                swiperRef?.slideTo(index)
              }}
              active={index === activeIndex}
            />
          ))}
        </NewStyledPaginator>
      )}
      {!newStyle && (
        <StyledPlayButton
          onClick={toggleAutoplay}
          aria-label={isPlaying ? "stop-playing-button" : "start-playing-button"}
        >
          {isPlaying ? <StopSign /> : <StartIcon />}
        </StyledPlayButton>
      )}
      {expandIcon && (
        <StyledGalleryDrawer
          open={isExpanded}
          onClose={() => setIsExpanded(false)}
          anchor={"top"}
          transitionDuration={800}
          sx={{ zIndex: 10001 }}
          SlideProps={{
            easing: "cubic-bezier(0.25, 1, 0.5, 1)",
          }}
          PaperProps={{
            sx: {
              width: "100%",
              height: "100%",
              borderRadius: 0,
            },
          }}
        >
          <SliderExpand setIsOpen={setIsExpanded} activeGallery={activeGallery} />
        </StyledGalleryDrawer>
      )}
    </Container>
  )
}
export default Slider
